// ============ CONTEXTO GLOBAL PARA OPERACIONES OFFLINE Y CONECTIVIDAD ============
// client/src/lib/contexts/offline-context.tsx

import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
} from "react";

// ============ INTERFAZ DEL CONTEXTO ============
interface OfflineContextType {
  // Estado de conectividad
  isOnline: boolean;
  isServerReachable: boolean | null;
  isOfflineMode: boolean;
  checkServer: () => Promise<boolean>;

  // Contadores de operaciones pendientes
  pendingOperationsCount: number;
  pendingSalesCount: number;
  pendingInventoryCount: number;
  incrementPending: (type?: "inventory" | "product" | "sale") => void;
  decrementPending: (type?: "inventory" | "product" | "sale") => void;
  resetPending: () => void;
}

const OfflineContext = createContext<OfflineContextType | undefined>(undefined);

// ============ PROVIDER ============
export function OfflineProvider({ children }: { children: ReactNode }) {
  // ============ ESTADOS DE CONECTIVIDAD ============
  const [isServerReachable, setIsServerReachable] = useState<boolean | null>(
    null,
  );
  const [isBrowserOnline, setIsBrowserOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );

  // ============ CONTADORES DE OPERACIONES PENDIENTES ============
  const [pendingOperationsCount, setPendingOperationsCount] = useState(0);
  const [pendingSalesCount, setPendingSalesCount] = useState(0);
  const [pendingInventoryCount, setPendingInventoryCount] = useState(0);

  // ============ FUNCIONES DE CONECTIVIDAD ============
  const checkServer = useCallback(async () => {
    // {Si el navegador dice que está offline, no intentar fetch}
    if (!navigator.onLine) {
      setIsServerReachable(false);
      return false;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2000);

      // {Usar fetch silencioso: no mostrar errores en consola}
      const response = await fetch("/api/health", {
        method: "HEAD",
        signal: controller.signal,
        cache: "no-cache",
      }).catch(() => null); // {null = fallo silencioso, no error en consola}

      clearTimeout(timeoutId);
      const isReachable = response !== null && response.ok;
      setIsServerReachable(isReachable);
      return isReachable;
    } catch {
      // {Error esperado cuando estamos offline, no hacer nada}
      setIsServerReachable(false);
      return false;
    }
  }, []);

  // ============ MONITOREAR CONEXIÓN DEL NAVEGADOR ============
  useEffect(() => {
    const handleOnline = () => setIsBrowserOnline(true);
    const handleOffline = () => setIsBrowserOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // ============ VERIFICAR SERVIDOR PERIÓDICAMENTE ============
  useEffect(() => {
    checkServer();
    const interval = setInterval(checkServer, 30000);
    return () => clearInterval(interval);
  }, [checkServer]);

  // ============ CALCULAR MODO OFFLINE ============
  const isOnline = isBrowserOnline && isServerReachable !== false;
  const isOfflineMode = !isOnline;

  // ============ FUNCIONES DE CONTADORES ============
  const incrementPending = useCallback(
    (type?: "inventory" | "product" | "sale") => {
      setPendingOperationsCount((prev) => prev + 1);

      if (type === "sale") {
        setPendingSalesCount((prev) => prev + 1);
      } else if (type === "inventory") {
        setPendingInventoryCount((prev) => prev + 1);
      }
    },
    [],
  );

  const decrementPending = useCallback(
    (type?: "inventory" | "product" | "sale") => {
      setPendingOperationsCount((prev) => Math.max(0, prev - 1));

      if (type === "sale") {
        setPendingSalesCount((prev) => Math.max(0, prev - 1));
      } else if (type === "inventory") {
        setPendingInventoryCount((prev) => Math.max(0, prev - 1));
      }
    },
    [],
  );

  const resetPending = useCallback(() => {
    setPendingOperationsCount(0);
    setPendingSalesCount(0);
    setPendingInventoryCount(0);
  }, []);

  return (
    <OfflineContext.Provider
      value={{
        // Conectividad
        isOnline,
        isServerReachable,
        isOfflineMode,
        checkServer,
        // Contadores
        pendingOperationsCount,
        pendingSalesCount,
        pendingInventoryCount,
        incrementPending,
        decrementPending,
        resetPending,
      }}
    >
      {children}
    </OfflineContext.Provider>
  );
}

// ============ HOOK PERSONALIZADO ============
export function useOfflineContext() {
  const context = useContext(OfflineContext);
  if (context === undefined) {
    throw new Error("useOfflineContext must be used within an OfflineProvider");
  }
  return context;
}
